import { Vue, Component, Prop } from "@wagich/vue-facing-decorator-metadata";
import debounce from "lodash/debounce";
import { markRaw } from "vue";

import { render } from "./site-search.html";
import type { SearchResponse, SearchItem, SearchFacet } from "../models/Search";
import { PersonDetail, type PersonDetailModal } from "~/vue/person-detail";

interface SearchHistoryState {
	globalSearch?: boolean;
	person?: boolean;
}

type ViewTransitionDocument = Document & {
	startViewTransition?: (update: () => Promise<void> | void) => { finished: Promise<void> };
};

// Below this the search runs as a full-screen layer instead of a dropdown (see docs/site-search.md).
const layerMedia = window.matchMedia("(max-width: 768px), (max-height: 640px)");
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
// Only iOS needs the layer to follow the visual viewport (see onVisualViewportChange). iPadOS reports
// itself as a Mac with touch points.
const isIos = /iP(ad|hone|od)/.test(navigator.userAgent) || (navigator.userAgent.includes("Macintosh") && navigator.maxTouchPoints > 1);
const instances = new Set<SiteSearch>();
let nextListId = 0;

// Icon shown in the media tile of a result without an image, by document type (sprite names).
const typeIcons: Record<string, string> = {
	"Person": "account",
	"Fach": "music",
	"Programm/Gruppe": "group",
	"Schulangebot": "group",
	"Anlass": "calendar",
	"Seite": "list",
	"Stelle": "registration",
};
const fallbackTypeIcon = "info";

function currentState(): SearchHistoryState {
	return (history.state ?? {}) as SearchHistoryState;
}

// A fresh page load never starts with an active search, so a state left behind by an earlier
// activation (e.g. returning to this entry without bfcache) is stale and must not swallow a back navigation.
if (currentState().globalSearch) {
	history.replaceState(null, "");
}

@Component({
	render,
})
export class SiteSearch extends Vue {
	@Prop({ type: Boolean, default: false }) detached: boolean;
	@Prop({ default: "Suchen …" }) placeholder: string;
	/** Shown in the mobile layer while nothing has been typed. */
	@Prop({ default: "Gib einen Suchbegriff ein, um Fächer, Angebote, Personen, Anlässe und Seiten zu finden." }) hint: string;
	/** Endpoint answering in the shape of SearchController.SearchResponse. */
	@Prop({ default: "/api/search" }) source: string;
	/** Comma-separated document types the search is restricted to; a single type hides the type switcher. */
	@Prop({ default: "" }) types: string;
	/** Open results in a new tab (downloads in the knowledge base). */
	@Prop({ type: Boolean, default: false }) newTab: boolean;

	listId = `site-search-list-${nextListId++}`;
	query = "";
	activeType: string | null = null;
	isLoading = false;
	/** From the first keystroke until the matching response arrives, so the debounce window counts as searching. */
	isSearching = false;
	hasSearched = false;
	items: SearchItem[] = [];
	facets: SearchFacet[] = [];
	found = 0;
	totalFound = 0;
	page = 1;
	highlightedIndex = -1;
	isLayer = layerMedia.matches;
	isActive = false;
	isActivating = false;
	isTransitioning = false;

	private personModal: PersonDetailModal | null = null;
	private requestId = 0;
	private readonly perPage = 16;

	/** The full-screen layer of small screens. The header button's instance uses it on every size (a sheet with backdrop on desktop). */
	get isLayerMode(): boolean {
		return this.isLayer || this.detached;
	}

	/** Desktop: the field lifts to the top of the viewport on focus and the results open below it. */
	get isLiftedMode(): boolean {
		return !this.isLayerMode;
	}

	get rootClasses(): Record<string, boolean> {
		return {
			"is-detached": this.detached,
			"is-mode-layer": this.isLayerMode,
			"is-mode-lifted": this.isLiftedMode,
			"is-active": this.isActive,
			"is-transitioning": this.isTransitioning,
			"is-viewport-tracked": isIos,
		};
	}

	get restrictedTypes(): string[] {
		return this.types.split(",").map(t => t.trim()).filter(t => t.length > 0);
	}

	get showChips(): boolean {
		return this.facets.length > 0 && this.restrictedTypes.length !== 1;
	}

	/** The type tag on each row is redundant while a type filter applies. */
	get showTypes(): boolean {
		return this.activeType == null && this.restrictedTypes.length !== 1;
	}

	get hasQuery(): boolean {
		return this.query.trim().length > 0;
	}

	get hasResults(): boolean {
		return this.items.length > 0;
	}

	get hasMore(): boolean {
		return this.items.length < this.found;
	}

	get showPanel(): boolean {
		return this.isActive;
	}

	/** A hint instead of an empty area while nothing has been typed. */
	get showHint(): boolean {
		return !this.hasQuery;
	}

	searchDebounced = debounce(() => this.runSearch(true), 250);

	// Event handlers are prototype methods on purpose: Vue binds methods to the component instance,
	// which makes `this.onPopState` a stable reference for add/removeEventListener.

	onMediaChange() {
		this.isLayer = layerMedia.matches;
		if (this.isActive || this.isActivating) {
			this.close();
		}
	}

	onDocumentPointerDown(e: PointerEvent) {
		if (!this.isActive) {
			return;
		}
		// Lifted: any click beside the card. Layer: only the desktop sheet of the header button leaves
		// room for a click on the backdrop; on phones the sheet covers the viewport.
		if (this.isOutside(e.target)) {
			this.close();
		}
	}

	onFocusOut(e: FocusEvent) {
		if (!this.isLiftedMode || !this.isActive) {
			return;
		}
		// Focus moving elsewhere (keyboard users tabbing past the results) closes the lifted search;
		// a lost focus without a successor (window blur, click on a non-focusable spot) does not.
		if (e.relatedTarget != null && this.isOutside(e.relatedTarget)) {
			this.close();
		}
	}

	onDocumentKeydown(e: KeyboardEvent) {
		if (e.key === "Escape" && this.isActive && !this.hasModalAbove()) {
			e.preventDefault();
			this.close();
		}
	}

	/**
	 * iOS never shrinks the layout viewport for the keyboard: it pans the visual viewport to reveal the
	 * focused field, which happens before the layer exists, and keeps that offset while the keyboard is
	 * open. A fixed full-height layer is then shifted off the top. So on iOS, while active, the layer
	 * follows the visual viewport. Android is left alone: Chrome resizes the visual viewport in steps
	 * while the keyboard animates, which made a following layer jump; there the scroller's extra
	 * bottom padding (see the stylesheet) keeps the last rows reachable above the keyboard.
	 */
	onVisualViewportChange() {
		var viewport = window.visualViewport;
		if (!isIos || viewport == null || !this.isActive || !layerMedia.matches) {
			return;
		}
		var style = (this.$el as HTMLElement).style;
		style.top = `${viewport.offsetTop}px`;
		style.height = `${viewport.height}px`;
		style.bottom = "auto";
	}

	private clearVisualViewportStyles() {
		var style = (this.$el as HTMLElement).style;
		style.top = "";
		style.height = "";
		style.bottom = "";
	}

	onPopState() {
		var state = currentState();
		if (this.personModal != null && !state.person) {
			var modal = this.personModal;
			this.personModal = null;
			modal.close();
		}
		if (!state.globalSearch) {
			this.deactivate();
		}
	}

	mounted() {
		instances.add(this);
		layerMedia.addEventListener("change", this.onMediaChange);
		document.addEventListener("pointerdown", this.onDocumentPointerDown);
	}

	unmounted() {
		instances.delete(this);
		layerMedia.removeEventListener("change", this.onMediaChange);
		document.removeEventListener("pointerdown", this.onDocumentPointerDown);
		window.removeEventListener("popstate", this.onPopState);
		document.removeEventListener("keydown", this.onDocumentKeydown);
		window.visualViewport?.removeEventListener("resize", this.onVisualViewportChange);
		window.visualViewport?.removeEventListener("scroll", this.onVisualViewportChange);
	}

	// --- input ---------------------------------------------------------------------------------

	onInput(e: Event) {
		this.query = (e.target as HTMLInputElement).value;
		if (!this.hasQuery) {
			this.searchDebounced.cancel();
			this.requestId++;
			this.isLoading = false;
			this.isSearching = false;
			this.resetResults(false);
			return;
		}
		this.isSearching = true;
		this.searchDebounced();
	}

	onFocus() {
		this.activate();
	}

	onKeydown(e: KeyboardEvent) {
		switch (e.key) {
			case "ArrowDown":
				e.preventDefault();
				this.moveHighlight(1);
				break;
			case "ArrowUp":
				e.preventDefault();
				this.moveHighlight(-1);
				break;
			case "Enter":
				if (this.highlightedIndex >= 0 && this.highlightedIndex < this.items.length) {
					e.preventDefault();
					this.select(this.items[this.highlightedIndex]);
				} else if (this.isLayerMode) {
					// The soft keyboard's search key: drop the keyboard so the whole list becomes visible.
					e.preventDefault();
					this.getInput()?.blur();
				}
				break;
			case "Escape":
				e.preventDefault();
				this.close();
				break;
		}
	}

	onSubmit() {
		// Enter is handled in onKeydown; the form only exists for its role and the soft keyboard's search key.
	}

	private moveHighlight(delta: number) {
		if (!this.hasResults) {
			return;
		}
		var count = this.items.length;
		var next = this.highlightedIndex < 0
			? (delta > 0 ? 0 : count - 1)
			: (this.highlightedIndex + delta + count) % count;
		this.highlightedIndex = next;
		this.$nextTick(() => {
			(this.$el as HTMLElement).querySelector(".is-highlighted")?.scrollIntoView({ block: "nearest" });
		});
	}

	// --- results -------------------------------------------------------------------------------

	onResultClick(e: MouseEvent, item: SearchItem) {
		if (item.url == null) {
			e.preventDefault();
			return;
		}
		var url = new URL(item.url, window.location.origin);
		if (!url.pathname.startsWith("/api/persons/")) {
			// Plain navigation; leaving the link native keeps middle-click and long-press working.
			return;
		}
		e.preventDefault();
		this.openPerson(url);
	}

	select(item: SearchItem) {
		if (item.url == null) {
			return;
		}
		var url = new URL(item.url, window.location.origin);
		if (url.pathname.startsWith("/api/persons/")) {
			this.openPerson(url);
			return;
		}
		if (this.newTab) {
			window.open(item.url, "_blank", "noopener");
			return;
		}
		window.location.href = item.url;
	}

	private async openPerson(url: URL) {
		if (!this.isLayerMode || !this.isActive) {
			// Lifted: the modal stacks above the search, which stays as it is.
			await PersonDetail.openFromUrl(url);
			return;
		}

		// The modal gets its own history entry so the back gesture closes it before the search.
		history.pushState({ globalSearch: true, person: true } satisfies SearchHistoryState, "");
		var modal = await PersonDetail.openFromUrl(url, { placeFocusBack: false });
		if (modal == null) {
			if (currentState().person) {
				history.back();
			}
			return;
		}
		this.personModal = markRaw(modal);
		modal.on("close", () => {
			this.personModal = null;
			if (currentState().person) {
				history.back();
			}
		});
	}

	iconFor(docType: string): string {
		return typeIcons[docType] ?? fallbackTypeIcon;
	}

	setType(
type: string | null) {
		if (this.activeType === type) {
			return;
		}
		this.activeType = type;
		this.searchDebounced.cancel();
		this.runSearch(true);
	}

	loadMore() {
		this.runSearch(false);
	}

	private buildUrl(term: string, page: number): string {
		var params = new URLSearchParams();
		params.set("q", term);
		params.set("page", String(page));
		params.set("perPage", String(this.perPage));
		if (this.restrictedTypes.length > 0) {
			params.set("types", this.restrictedTypes.join(","));
		}
		if (this.activeType != null) {
			params.set("type", this.activeType);
		}
		return `${this.source}${this.source.includes("?") ? "&" : "?"}${params.toString()}`;
	}

	private resetResults(keepSearched: boolean) {
		this.items = [];
		this.facets = [];
		this.found = 0;
		this.totalFound = 0;
		this.page = 1;
		this.highlightedIndex = -1;
		this.hasSearched = keepSearched;
	}

	async runSearch(reset: boolean) {
		var term = this.query.trim();
		if (term.length === 0) {
			this.isSearching = false;
			this.resetResults(false);
			return;
		}

		var nextPage = reset ? 1 : this.page + 1;
		var id = ++this.requestId;
		this.isLoading = true;
		if (reset) {
			this.isSearching = true;
		}
		try {
			var response = await fetch(this.buildUrl(term, nextPage), {
				method: "GET",
				headers: { "Accept": "application/json" },
			});
			if (id !== this.requestId) {
				return;
			}
			if (!response.ok) {
				if (reset) {
					this.resetResults(true);
				}
				return;
			}
			var data = (await response.json()) as SearchResponse;
			if (id !== this.requestId) {
				return;
			}
			this.found = data.found;
			this.totalFound = data.totalFound ?? data.found;
			this.page = data.page;
			this.items = reset ? data.items : this.items.concat(data.items);
			this.facets = data.facets;
			if (reset) {
				this.highlightedIndex = -1;
				(this.$refs.scroller as HTMLElement | undefined)?.scrollTo(0, 0);
			}
		} catch {
			if (id === this.requestId && reset) {
				this.resetResults(true);
			}
		} finally {
			if (id === this.requestId) {
				this.isLoading = false;
				this.isSearching = false;
				this.hasSearched = true;
			}
		}
	}

	// --- active state (mobile layer, desktop lifted search) ---------------------------------------

	/** True for a node outside this search, ignoring the person modal that may stack above it. */
	private isOutside(node: EventTarget | null): boolean {
		if (!(node instanceof Node)) {
			return false;
		}
		if ((this.$el as HTMLElement).contains(node)) {
			return false;
		}
		return !(node instanceof Element && node.closest(".fancybox__dialog, .fancybox__container") != null);
	}

	private hasModalAbove(): boolean {
		return document.querySelector(".fancybox__container") != null;
	}

	private getInput(): HTMLInputElement | undefined {
		return this.$refs.input as HTMLInputElement | undefined;
	}

	private getHost(): HTMLElement | null {
		var parent = (this.$el as HTMLElement).parentElement;
		return parent?.tagName === "SITE-SEARCH" ? parent : null;
	}

	/** Entry point for the header button: focuses the field, which activates the layer. */
	activateExternal() {
		var host = this.getHost();
		if (host != null) {
			host.hidden = false;
		}
		var input = this.getInput();
		if (input != null && document.activeElement !== input) {
			input.focus({ preventScroll: true });
		}
		this.activate();
	}

	private activate() {
		if (this.isActive || this.isActivating) {
			return;
		}
		this.isActivating = true;
		// Names the bar for the view transition; only the instance that animates may carry the name.
		this.isTransitioning = true;
		if (this.isLayerMode) {
			// The layer gets a history entry so the back gesture closes it; the lifted search is a
			// dropdown as far as navigation is concerned.
			history.pushState({ globalSearch: true } satisfies SearchHistoryState, "");
			window.addEventListener("popstate", this.onPopState);
			window.visualViewport?.addEventListener("resize", this.onVisualViewportChange);
			window.visualViewport?.addEventListener("scroll", this.onVisualViewportChange);
		}
		document.addEventListener("keydown", this.onDocumentKeydown);
		this.transition(() => {
			if (this.isLayerMode) {
				document.documentElement.classList.add("has-search-layer");
			}
			this.isActive = true;
			this.showInTopLayer();
			this.onVisualViewportChange();
		}).finally(() => {
			this.isActivating = false;
		});
	}

	private async deactivate() {
		if (!this.isActive && !this.isActivating) {
			return;
		}
		window.removeEventListener("popstate", this.onPopState);
		document.removeEventListener("keydown", this.onDocumentKeydown);
		window.visualViewport?.removeEventListener("resize", this.onVisualViewportChange);
		window.visualViewport?.removeEventListener("scroll", this.onVisualViewportChange);
		this.getInput()?.blur();
		await this.transition(() => {
			document.documentElement.classList.remove("has-search-layer");
			this.isActive = false;
			this.hideFromTopLayer();
			this.clearVisualViewportStyles();
		});
		this.isTransitioning = false;
		var host = this.getHost();
		if (this.detached && host != null) {
			host.hidden = true;
		}
	}

	/** Closes the search; the layer goes through history so the back gesture and the close button share one path. */
	close() {
		if (!this.isActive && !this.isActivating) {
			return;
		}
		if (currentState().globalSearch) {
			history.back();
		} else {
			this.deactivate();
		}
	}

	// The layer is promoted to the top layer so it renders above the fixed header tools regardless of
	// the stacking context it lives in (the homepage field sits inside the cover). The input node is
	// never moved, so the focus and the on-screen keyboard survive activation.
	private showInTopLayer() {
		var el = this.$el as HTMLElement;
		if (typeof el.showPopover === "function") {
			el.setAttribute("popover", "manual");
			el.showPopover();
		}
	}

	private hideFromTopLayer() {
		var el = this.$el as HTMLElement;
		if (el.hasAttribute("popover")) {
			if (el.matches(":popover-open")) {
				el.hidePopover();
			}
			el.removeAttribute("popover");
		}
	}

	private transition(update: () => void): Promise<void> {
		var doc = document as ViewTransitionDocument;
		if (typeof doc.startViewTransition !== "function" || reducedMotion.matches) {
			update();
			return this.$nextTick();
		}
		try {
			var viewTransition = doc.startViewTransition(async () => {
				update();
				await this.$nextTick();
			});
			return viewTransition.finished.catch(() => undefined);
		} catch {
			update();
			return this.$nextTick();
		}
	}

	/**
	 * Opens the site search from the header button: a dedicated hidden instance that is created on
	 * first use and reused afterwards, independent of any search field the page itself renders.
	 * It always uses the layer presentation, on desktop as a sheet with a backdrop.
	 */
	static open() {
		var instance = [...instances].find(i => i.detached);
		if (instance == null) {
			var host = document.createElement("site-search");
			host.setAttribute("detached", "");
			host.hidden = true;
			document.body.appendChild(host);
			instance = [...instances].find(i => i.detached);
		}
		instance?.activateExternal();
	}
}
